/**
 * Email verification page component.
 */

import { useEffect, useState, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { VerificationFlow } from '@ory/client';
import { kratos, getFlowId, handleKratosError } from '../lib/kratos';
import FormField from '../components/FormField';
import FlowMessages from '../components/FlowMessages';
import LoadingSpinner from '../components/LoadingSpinner';

export default function Verification() {
  const location = useLocation();
  const navigate = useNavigate();
  const [flow, setFlow] = useState<VerificationFlow | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verified, setVerified] = useState(false);

  const initializeFlow = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const flowId = getFlowId(location.search);

      if (flowId) {
        try {
          const { data } = await kratos.getVerificationFlow({ id: flowId });
          setFlow(data);
          if (data.state === 'passed_challenge') {
            setVerified(true);
          }
          return;
        } catch {
          // Flow expired or invalid — create a fresh one below
        }
      }

      const { data } = await kratos.createBrowserVerificationFlow();
      navigate(`/verification?flow=${data.id}`, { replace: true });
      setFlow(data);
    } catch (err) {
      setError(handleKratosError(err));
    } finally {
      setIsLoading(false);
    }
  }, [location.search, navigate]);

  useEffect(() => {
    initializeFlow();
  }, [initializeFlow]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const formData = new FormData(event.currentTarget);
    const values: Record<string, string> = {};
    formData.forEach((value, key) => {
      values[key] = value.toString();
    });

    try {
      const { data } = await kratos.updateVerificationFlow({
        flow: flow!.id,
        updateVerificationFlowBody: values as unknown as Parameters<typeof kratos.updateVerificationFlow>[0]['updateVerificationFlowBody'],
      });

      setFlow(data);

      if (data.state === 'passed_challenge') {
        setVerified(true);
      }
    } catch (err) {
      const errorMessage = handleKratosError(err);
      if (errorMessage.includes('expired') || errorMessage.includes('invalid')) {
        initializeFlow();
      }
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner text="Loading..." />
      </div>
    );
  }

  if (error && !flow) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card max-w-md w-full text-center">
          <h1 className="text-xl font-bold text-red-600 dark:text-red-400 mb-2">Error</h1>
          <p className="text-content-secondary mb-4">{error}</p>
          <button onClick={initializeFlow} className="btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (verified) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-content-primary mb-2">Email Verified</h1>
          <p className="text-content-secondary mb-6">
            Your email address has been successfully verified.
          </p>
          <Link to="/login" className="btn-primary">
            Continue to Login
          </Link>
        </div>
      </div>
    );
  }

  // Get form nodes
  const defaultNodes = flow?.ui.nodes.filter((n) => n.group === 'default') || [];
  const codeNodes = flow?.ui.nodes.filter((n) => n.group === 'code') || [];

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="card max-w-md w-full">
        <div className="text-center mb-8">
          <img
            src="/orbital-passport-lm.svg"
            alt="OrbitalPassport"
            className="h-16 w-16 mx-auto mb-4 dark:hidden"
          />
          <img
            src="/orbital-passport-transparent.svg"
            alt="OrbitalPassport"
            className="h-16 w-16 mx-auto mb-4 hidden dark:block"
          />
          <h1 className="text-2xl font-bold text-content-primary">Verify Email</h1>
          <p className="text-content-secondary mt-1">
            Enter your email to receive a verification code
          </p>
        </div>

        <FlowMessages messages={flow?.ui.messages} />

        {error && (
          <div className="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Hidden fields */}
          {defaultNodes
            .filter((n) => (n.attributes as { type?: string }).type === 'hidden')
            .map((node, i) => (
              <FormField key={i} node={node} disabled={isSubmitting} />
            ))}

          {/* Email/code fields */}
          {[...defaultNodes, ...codeNodes]
            .filter((n) => (n.attributes as { type?: string }).type !== 'hidden' &&
                          (n.attributes as { type?: string }).type !== 'submit')
            .map((node, i) => (
              <FormField key={i} node={node} disabled={isSubmitting} />
            ))}

          {/* Submit button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="btn-primary w-full flex items-center justify-center"
          >
            {isSubmitting ? <LoadingSpinner size="sm" /> : 'Verify Email'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300 font-medium"
          >
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
}
